/**
 * ObligationsTab — Platform Console · externally-IMPOSED requirements.
 *
 * Where Values are chosen, Obligations are imposed (law, regulation,
 * contract, professional standards). Reads
 * `/api/release-ops/obligations-registry` (fed by
 * `/app/memory/obligations_registry.md`).
 *
 * Sub-tabs: Overview + one per obligation_group. Empty groups render an
 * explicit empty state — the taxonomy exists ahead of its entries.
 */
import { useEffect, useMemo, useState } from "react";
import { Scale, RefreshCw, LayoutDashboard } from "lucide-react";
import { api } from "@/lib/api";
import { SubTabs } from "@/components/site-admin/values-registry/RegistryShared";

const GROUPS = [
    { slug: "obligations/legal",            k: "legal",            label: "Legal" },
    { slug: "obligations/tax",              k: "tax",              label: "Tax" },
    { slug: "obligations/regulatory",       k: "regulatory",       label: "Regulatory" },
    { slug: "obligations/privacy-security", k: "privacy_security", label: "Privacy & Security" },
    { slug: "obligations/accessibility",    k: "accessibility",    label: "Accessibility" },
    { slug: "obligations/contractual",      k: "contractual",      label: "Contractual" },
];

const STATE_COLORS = {
    "compliant":             "bg-emerald-900/40 text-emerald-200 border-emerald-700",
    "non-compliant":         "bg-rose-900/40 text-rose-200 border-rose-700",
    "investigating":         "bg-amber-900/40 text-amber-200 border-amber-700",
    "consult-professional":  "bg-violet-900/40 text-violet-200 border-violet-700",
    "not-applicable":        "bg-zinc-800 text-zinc-400 border-zinc-700",
};

const SEVERITY_COLORS = {
    critical: "bg-rose-600/20 text-rose-300 border-rose-500/60",
    high:     "bg-rose-500/10 text-rose-400 border-rose-500/40",
    med:      "bg-amber-500/10 text-amber-400 border-amber-500/40",
    low:      "bg-zinc-700/30 text-zinc-400 border-zinc-700",
};

const POSTURE_COLORS = {
    blocked: "bg-rose-500/15 text-rose-300 border-rose-500/40",
    warn:    "bg-amber-500/15 text-amber-300 border-amber-500/40",
    notice:  "bg-sky-500/15 text-sky-300 border-sky-500/40",
    ok:      "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
};

function StatePill({ state }) {
    const cls = STATE_COLORS[state] || "bg-zinc-800 text-zinc-300 border-zinc-700";
    return (
        <span className={`pill text-[9px] font-bold uppercase border ${cls}`} data-testid={`obligation-state-pill-${state}`}>
            {state}
        </span>
    );
}

function ObligationRow({ row }) {
    return (
        <div
            className="border border-zinc-800 rounded-md p-3 text-xs bg-black/40"
            data-testid={`obligation-row-${row.name}`}
        >
            <div className="flex items-center gap-2 flex-wrap mb-1">
                <code className="text-sm font-bold text-white">{row.name}</code>
                <StatePill state={row.compliance_state} />
                {row.severity && (
                    <span
                        className={`pill text-[9px] font-bold uppercase border ${SEVERITY_COLORS[row.severity] || SEVERITY_COLORS.med}`}
                        title="Severity — governed in Stewardship → Severity (read-only here)"
                        data-testid={`obligation-severity-pill-${row.name}`}
                    >
                        {row.severity}
                    </span>
                )}
                {row.risk_posture && (
                    <span
                        className={`pill text-[9px] font-bold uppercase border ${POSTURE_COLORS[row.risk_posture] || POSTURE_COLORS.ok}`}
                        title="Risk-if-deployed posture = compliance_state × severity"
                        data-testid={`obligation-posture-pill-${row.name}`}
                    >
                        {row.risk_posture}
                    </span>
                )}
                <span className="pill text-[9px] font-bold uppercase border bg-zinc-800 text-zinc-300 border-zinc-700">
                    {row.jurisdiction}
                </span>
            </div>
            <div className="text-zinc-300 mb-2">{row.description}</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-[10px] text-zinc-500">
                <div><span className="text-zinc-600">Authority:</span> <span className="text-zinc-400">{row.authority}</span></div>
                <div><span className="text-zinc-600">Owner:</span> <span className="text-zinc-400">{row.owner}</span></div>
                <div><span className="text-zinc-600">Last review:</span> <span className="text-zinc-400">{row.last_review || "—"}</span></div>
                {row.notes && <div><span className="text-zinc-600">Notes:</span> <span className="text-zinc-400">{row.notes}</span></div>}
            </div>
        </div>
    );
}

function OverviewView({ data }) {
    const byState = data.counts?.by_compliance_state || {};
    const byGroup = data.counts?.by_obligation_group || {};
    return (
        <div className="space-y-4" data-testid="obligations-overview-view">
            <div className="flex flex-wrap items-center gap-3 text-[11px] text-zinc-400" data-testid="obligations-counts-strip">
                <span>
                    <span className="text-zinc-500">Active:</span>{" "}
                    <span className="text-white font-bold" data-testid="obligations-counts-active">{data.counts?.total_active ?? 0}</span>
                </span>
                <span>
                    <span className="text-zinc-500">Retired:</span>{" "}
                    <span className="text-white font-bold">{data.counts?.total_retired ?? 0}</span>
                </span>
                <span className="text-zinc-700">|</span>
                {Object.keys(STATE_COLORS).map((s) => (
                    <span key={s} className="flex items-center gap-1">
                        <StatePill state={s} />
                        <span className="text-white font-bold">{byState[s] ?? 0}</span>
                    </span>
                ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {GROUPS.map((g) => {
                    const node = data.obligation_groups?.[g.slug];
                    if (!node) return null;
                    return (
                        <div
                            key={g.slug}
                            className="border-2 border-cyan-900/40 rounded-lg p-4 bg-cyan-950/10"
                            data-testid={`obligations-overview-card-${g.k}`}
                        >
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                                <h3 className="font-display font-extrabold text-xs uppercase tracking-wide text-cyan-200">
                                    {g.slug}
                                </h3>
                                <span className="ml-auto text-[10px] text-zinc-500">
                                    {byGroup[g.slug] ?? 0} active
                                </span>
                            </div>
                            <p className="text-xs text-zinc-300 italic leading-relaxed">{node.statement}</p>
                        </div>
                    );
                })}
            </div>

            <p className="text-[10px] text-zinc-600">
                Obligations are <span className="text-zinc-400 font-bold">imposed</span> — law, regulation, contract —
                honored whether the crew believes in them or not. Chosen standards live in the Values tab.
                Source: <code>{data.source}</code>
            </p>
        </div>
    );
}

function GroupView({ data, group }) {
    const node = data.obligation_groups?.[group.slug];
    const rows = node?.children ?? [];
    return (
        <div className="space-y-3" data-testid={`obligations-group-view-${group.k}`}>
            <div className="border-2 border-cyan-900/40 rounded-lg p-4 bg-cyan-950/10">
                <h3 className="font-display font-extrabold text-sm uppercase tracking-wide text-cyan-200 mb-1">
                    {group.slug}
                </h3>
                <p className="text-xs text-zinc-300 italic">{node?.statement}</p>
            </div>
            {rows.length === 0 ? (
                <div
                    className="border border-dashed border-zinc-700 rounded-lg p-6 text-center text-xs text-zinc-500"
                    data-testid={`obligations-group-empty-${group.k}`}
                >
                    No {group.label.toLowerCase()} obligations cataloged yet.
                    <div className="text-[10px] text-zinc-600 mt-1">
                        Add rows to <code>app/memory/obligations_registry.md</code> as requirements are identified — they render here automatically.
                    </div>
                </div>
            ) : (
                <div className="space-y-2">
                    {rows.map((r) => <ObligationRow key={r.name} row={r} />)}
                </div>
            )}
        </div>
    );
}

export default function ObligationsTab() {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [sub, setSub] = useState("overview");

    const load = async () => {
        setLoading(true);
        setError(null);
        try {
            setData(await api.releaseOpsObligationsRegistry());
        } catch (e) {
            setError(e?.response?.data?.detail || e?.message || "Failed to load");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const generatedAt = useMemo(() => {
        if (!data?.generated_at) return null;
        return data.generated_at.replace("T", " ").replace(/\.\d+Z?$/, "Z");
    }, [data]);

    const subItems = [
        { k: "overview", label: "Overview", icon: LayoutDashboard },
        ...GROUPS.map((g) => ({ k: g.k, label: g.label })),
    ];
    const activeGroup = GROUPS.find((g) => g.k === sub);

    return (
        <div className="space-y-4" data-testid="obligations-tab">
            <div className="brutal-card p-4 border-2 border-cyan-900/40 bg-cyan-950/10">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                            <Scale className="w-4 h-4 text-cyan-300" />
                            <h2 className="font-display font-extrabold text-lg uppercase tracking-wide text-white">
                                Obligations — Imposed Requirements
                            </h2>
                            <span className="pill text-[9px] font-bold uppercase border bg-cyan-900/40 text-cyan-200 border-cyan-700">
                                Compliance
                            </span>
                        </div>
                        <div className="text-xs text-zinc-400">
                            Legal, tax, regulatory, privacy, accessibility, and contractual requirements the world imposes on the crew.
                            {generatedAt && (
                                <>
                                    {" "}<span className="text-zinc-600">·</span>{" "}
                                    <span className="text-zinc-500">Updated {generatedAt}</span>
                                </>
                            )}
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={load}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide bg-zinc-900 border border-zinc-700 rounded hover:text-white"
                        data-testid="obligations-refresh-btn"
                    >
                        <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
                        Refresh
                    </button>
                </div>
            </div>

            <SubTabs items={subItems} active={sub} onChange={setSub} testPrefix="obligations-subtab" />

            {error && (
                <div className="text-xs text-rose-300 bg-rose-950/40 border border-rose-800 rounded p-3" data-testid="obligations-error">
                    {error}
                </div>
            )}
            {!data && !error && (
                <div className="text-xs text-zinc-500" data-testid="obligations-loading">Loading registry…</div>
            )}

            {data && (
                <div className="brutal-card p-4">
                    {sub === "overview" && <OverviewView data={data} />}
                    {activeGroup && <GroupView data={data} group={activeGroup} />}
                </div>
            )}
        </div>
    );
}
